import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import ParticleXBackground from "@/components/ParticleXBackground";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { 
  Eye, 
  EyeOff, 
  Mail, 
  User, 
  Phone, 
  Briefcase, 
  MessageCircle,
  Search,
  Building2,
  CheckCircle2,
  Loader2,
  MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const userType = searchParams.get("type") || searchParams.get("role") || "client";
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: userType === "worker" ? "worker" : "client_admin",
    client_type: "provider",
    is_private_client: false,
  });
  
  const [orgNumber, setOrgNumber] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [companyFound, setCompanyFound] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (userType === "worker") {
      setFormData((prev) => ({ ...prev, role: "worker" }));
    }
  }, [userType]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOrgLookup = async () => {
    const cleanedOrg = orgNumber.replace(/\s/g, "").replace(/\./g, "");
    
    if (!cleanedOrg || cleanedOrg.length !== 9) {
      toast.error(t('errors.validationError'));
      return;
    }

    setLookingUp(true);
    try {
      const response = await axios.get(`${API}/lookup/company/${cleanedOrg}`);
      
      if (response.data.found) {
        setCompanyData({
          name: response.data.name,
          org_number: response.data.org_number,
          address: response.data.address,
          city: response.data.city,
          postal_code: response.data.postal_code,
          country: response.data.country || "Norway",
          website: response.data.website || "",
          industry: response.data.industry || "",
          employees: response.data.employees || 0,
        });
        setCompanyFound(true);
        toast.success(`${t('common.success')}: ${response.data.name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.notFound'));
      setCompanyFound(false);
      setCompanyData(null);
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.role === "client_admin" && !companyFound) {
      toast.error(t('errors.validationError'));
      return;
    }
    
    setLoading(true);

    try {
      const actualRole = formData.role === "client_private" ? "client_admin" : formData.role;
      const isPrivateClient = formData.role === "client_private";
      
      const submitData = {
        ...formData,
        role: actualRole,
        client_type: actualRole === "client_admin" ? (isPrivateClient ? "private" : "provider") : null,
      };
      
      const user = await register(submitData);
      
      if (formData.role === "client_admin" && companyData && !isPrivateClient) {
        try {
          await axios.post(`${API}/companies`, {
            ...companyData,
            contact_person_name: formData.name,
            contact_email: formData.email,
            contact_phone: formData.phone || "",
            company_type: "client",
            client_type: "provider",
          }, {
            headers: { Authorization: `Bearer ${user.token}` }
          });
        } catch (companyError) {
          console.error("Company creation error:", companyError);
        }
      }
      
      if (user.role === "worker" && !user.is_approved) {
        toast.success(t('auth.accountCreated'));
        navigate("/login");
      } else {
        toast.success(t('auth.accountCreated'));
        const routes = {
          platform_admin: "/admin",
          operations_manager: "/admin",
          team_leader: "/team-leader",
          worker: "/worker",
          client_admin: "/client",
          client_viewer: "/client",
          webshop_admin: "/admin/webshop",
          rentals_admin: "/admin/rentals",
        };
        navigate(routes[user.role] || "/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.role !== "client_admin") {
      setCompanyFound(false);
      setCompanyData(null);
      setOrgNumber("");
    }
  }, [formData.role]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <ParticleXBackground color="purple" opacity={0.4} />
      <div className="fixed inset-0 grid-bg opacity-20 pointer-events-none"></div>

      {/* Language Switcher - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-1 justify-center mb-8">
          <div className="px-3 py-1.5 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
            <span className="text-[#23F7DD] font-bold text-xl font-['Unbounded']">All</span>
          </div>
          <span className="text-2xl font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
        </Link>

        <div className="glass-card p-8">
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white text-center mb-2">
            {t('auth.createAccount')}
          </h1>
          <p className="text-[#94A3B8] text-center mb-8">
            {userType === "worker" ? t('auth.worker') : t('landing.cta.button')}
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const baseRedirect = window.location.origin;
              const redirectUrl = formData.role === "worker" 
                ? `${baseRedirect}/auth/callback?user_type=worker`
                : `${baseRedirect}/auth/callback?user_type=client`;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full mb-6 py-6 border-white/20 text-white hover:bg-white/5 hover:border-[#23F7DD]"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.signInWith', { provider: 'Google' })}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0A0A0A] text-[#94A3B8]">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-3">
              <Label className="text-[#94A3B8]">{t('auth.role')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {/* Scaffolding Company */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: "client_admin", is_private_client: false })}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    formData.role === "client_admin"
                      ? "border-[#23F7DD] bg-[#23F7DD]/20 ring-2 ring-[#23F7DD]/50"
                      : "border-white/20 bg-white/5 hover:border-white/40"
                  }`}
                >
                  <div className={`w-5 h-5 mx-auto rounded-full border-2 flex items-center justify-center mb-2 ${
                    formData.role === "client_admin"
                      ? "border-[#23F7DD] bg-[#23F7DD]"
                      : "border-white/40"
                  }`}>
                    {formData.role === "client_admin" && (
                      <div className="w-2 h-2 rounded-full bg-[#050505]" />
                    )}
                  </div>
                  <span className={`font-medium text-xs sm:text-sm block ${formData.role === "client_admin" ? "text-[#23F7DD]" : "text-white"}`}>
                    {t('register.scaffoldCompanyShort')}
                  </span>
                </button>
                
                {/* Private Client */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: "client_private", is_private_client: false })}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    formData.role === "client_private"
                      ? "border-[#4F46E5] bg-[#4F46E5]/20 ring-2 ring-[#4F46E5]/50"
                      : "border-white/20 bg-white/5 hover:border-white/40"
                  }`}
                >
                  <div className={`w-5 h-5 mx-auto rounded-full border-2 flex items-center justify-center mb-2 ${
                    formData.role === "client_private"
                      ? "border-[#4F46E5] bg-[#4F46E5]"
                      : "border-white/40"
                  }`}>
                    {formData.role === "client_private" && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className={`font-medium text-xs sm:text-sm block ${formData.role === "client_private" ? "text-[#4F46E5]" : "text-white"}`}>
                    {t('register.privateClientShort')}
                  </span>
                </button>
                
                {/* Worker */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: "worker", is_private_client: false })}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    formData.role === "worker"
                      ? "border-[#FF5F1F] bg-[#FF5F1F]/20 ring-2 ring-[#FF5F1F]/50"
                      : "border-white/20 bg-white/5 hover:border-white/40"
                  }`}
                >
                  <div className={`w-5 h-5 mx-auto rounded-full border-2 flex items-center justify-center mb-2 ${
                    formData.role === "worker"
                      ? "border-[#FF5F1F] bg-[#FF5F1F]"
                      : "border-white/40"
                  }`}>
                    {formData.role === "worker" && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className={`font-medium text-xs sm:text-sm block ${formData.role === "worker" ? "text-[#FF5F1F]" : "text-white"}`}>
                    {t('register.scaffoldWorkerShort')}
                  </span>
                </button>
              </div>
            </div>

            {formData.role === "client_private" && (
              <div className="p-4 rounded-lg border border-[#4F46E5]/30 bg-[#4F46E5]/10">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-[#4F46E5] mt-0.5" />
                  <div>
                    <span className="text-white font-medium">{t('register.privateAccount')}</span>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {t('register.privateAccountDesc')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.role === "worker" && (
              <div className="p-4 rounded-lg border border-[#FF5F1F]/30 bg-[#FF5F1F]/10">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-[#FF5F1F] mt-0.5" />
                  <div>
                    <span className="text-white font-medium">{t('register.importantInfo')}</span>
                    <p className="text-sm text-[#94A3B8] mt-1 mb-2">
                      {t('register.workerInfoDesc')}
                    </p>
                    <ul className="text-sm text-[#94A3B8] space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                        {t('register.legalContracts')}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                        {t('register.holidayPay')}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                        {t('register.taxDeducted')}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                        {t('register.pensionInsurance')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {formData.role === "client_admin" && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-[#23F7DD]/30 bg-[#23F7DD]/5">
                  <div className="flex items-start gap-3 mb-4">
                    <Building2 className="w-5 h-5 text-[#23F7DD] mt-0.5" />
                    <div>
                      <span className="text-white font-medium">{t('register.companyVerification')}</span>
                      <p className="text-sm text-[#94A3B8] mt-1">
                        {t('register.enterOrgNumber')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        value={orgNumber}
                        onChange={(e) => setOrgNumber(e.target.value)}
                        placeholder="123 456 789"
                        className="bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
                        data-testid="org-number-input"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleOrgLookup}
                      disabled={lookingUp || !orgNumber.trim()}
                      className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] px-4"
                      data-testid="org-lookup-btn"
                    >
                      {lookingUp ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {companyFound && companyData && (
                  <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10 animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">{t('register.companyVerified')}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Building2 className="w-4 h-4 text-[#94A3B8] mt-0.5" />
                        <div>
                          <p className="text-white font-medium">{companyData.name}</p>
                          <p className="text-[#94A3B8]">Org.nr: {companyData.org_number}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-[#94A3B8] mt-0.5" />
                        <p className="text-[#94A3B8]">
                          {companyData.address}, {companyData.postal_code} {companyData.city}
                        </p>
                      </div>
                      {companyData.industry && (
                        <p className="text-[#94A3B8] text-xs">
                          Bransje: {companyData.industry}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-[#94A3B8] mt-0.5" />
                    <div>
                      <span className="text-[#94A3B8] text-sm">{t('register.needScaffoldServices')}</span>
                      <div className="mt-2">
                        <a 
                          href="mailto:bogdan@doserne.com"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF5F1F] text-white rounded-lg hover:bg-[#FF5F1F]/80 transition-colors font-medium"
                          data-testid="contact-request-btn"
                        >
                          <Mail className="w-4 h-4" />
                          {t('register.requestQuote')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#94A3B8]">
                {formData.role === "client_admin" ? t('register.contactPerson') : t('auth.fullName')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('register.namePlaceholder')}
                  className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#94A3B8]">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="du@eksempel.no"
                  className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#94A3B8]">
                {t('auth.phoneNumber')} {formData.role === "client_admin" ? `(${t('common.required')})` : `(${t('common.optional')})`}
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+47 123 45 678"
                  className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20"
                  required={formData.role === "client_admin"}
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#94A3B8]">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pr-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#94A3B8]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {formData.role === "worker" && (
              <div className="p-4 bg-[#FF5F1F]/10 border border-[#FF5F1F]/30 rounded-lg">
                <p className="text-sm text-[#FF5F1F]">
                  {t('register.workerApprovalRequired')}
                </p>
              </div>
            )}

            {formData.role === "client_admin" && !companyFound && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-400">
                  {t('register.verifyCompany')}
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (formData.role === "client_admin" && !companyFound)}
              className="w-full py-6 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                  {t('common.loading')}
                </span>
              ) : (
                t('auth.createAccount')
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#94A3B8]">
            {t('auth.alreadyHaveAccount')}{" "}
            <Link to="/login" className="text-[#23F7DD] hover:underline font-medium">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
